import AccountSettingsForm from "@/components/settings/AccountSettingsForm";
import AppInstallCard from "@/components/settings/AppInstallCard";
import AppReloadCard from "@/components/settings/AppReloadCard";
import ContactSupportCard from "@/components/settings/ContactSupportCard";
import EmptyState from "@/components/EmptyState";
import MembershipManagement from "@/components/settings/MembershipManagement";
import Spinner from "@/components/Spinner";
import UpdateEmailSection from "@/components/settings/UpdateEmailSection";
import { StripePaymentStatus, SyncResult, UserGender } from "@/generated-api";
import { useStandaloneMode } from "@/hooks/useStandaloneMode";
import {
  useCheckStripeSessionStatus,
  useGetAccountSettings,
  useGetUserProfile,
  usePrefetchSubscriptionPlans,
  useSyncRevenueCatStatus,
} from "@/tanstack-hooks";
import { CloudOffIcon, InfoIcon } from "lucide-react";
import { isMobile } from "react-device-detect";
import ReferralCard from "@/components/settings/ReferralCard";
import { useTranslation } from "@/hooks/useTranslation";
import PushNotificationSettings from "@/components/settings/PushNotificationSettings";
import { useSearchParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useEffect } from "react";
import tada from "@/assets/tada.png";
import { Capacitor } from "@capacitor/core";

export default function AccountSettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const stripeSessionId = searchParams.get("stripe_session_id");
  const iapPurchase = searchParams.get("iap_purchase");
  const useCheckStripeStatusMutation = useCheckStripeSessionStatus();
  const syncRevenueCatMutation = useSyncRevenueCatStatus();
  const accountSettingsQuery = useGetAccountSettings();
  const userProfileQuery = useGetUserProfile();
  const isStandalone = useStandaloneMode();
  usePrefetchSubscriptionPlans();
  const { toast } = useToast();
  const t = useTranslation();

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (!stripeSessionId) return;

    useCheckStripeStatusMutation.mutate(
      { session_id: stripeSessionId },
      {
        onSuccess: (response) => {
          if (
            response.payment_status !==
            StripePaymentStatus.payment_status.UNPAID
          ) {
            toast({
              // @ts-expect-error Type 'Element' is not assignable to type
              title: (
                <div className="flex items-center gap-2">
                  <img src={tada} className="w-5 h-5" />
                  {t.PAYMENT_CONFIRMATION_DIALOG.LABELS.SUCCESS_MESSAGE}
                </div>
              ),
              variant: "default",
              className: "dark rounded-2xl",
            });
          } else {
            toast({
              title: t.PAYMENT_CONFIRMATION_DIALOG.LABELS.FAILURE_MESSAGE,
              description:
                t.PAYMENT_CONFIRMATION_DIALOG.LABELS.FAILURE_DESCRIPTION,
              variant: "destructive",
              className: "dark rounded-2xl",
            });
          }
        },
      },
    );

    searchParams.delete("stripe_session_id");
    setSearchParams(searchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeSessionId]);

  // iOS IAP return path: the checkout screen redirects here right after
  // Apple confirms the purchase (mirrors the Stripe return_url flow above).
  // We confirm the entitlement with the backend and toast the outcome.
  // Never show a payment failure — Apple already charged the user, and the
  // webhook backstop reconciles shortly; a failure toast could nudge them
  // into paying twice.
  useEffect(() => {
    if (!iapPurchase) return;

    function showPurchaseProcessingToast() {
      toast({
        // @ts-expect-error Type 'Element' is not assignable to type
        title: (
          <div className="flex items-center gap-2">
            <InfoIcon className="w-5 h-5" />
            {t.PAYMENT_CONFIRMATION_DIALOG.LABELS.PURCHASE_PROCESSING_MESSAGE}
          </div>
        ),
        description:
          t.PAYMENT_CONFIRMATION_DIALOG.LABELS.PURCHASE_PROCESSING_DESCRIPTION,
        variant: "default",
        className: "dark rounded-2xl",
      });
    }

    syncRevenueCatMutation.mutate(null, {
      onSuccess: (data) => {
        if (data === SyncResult.ACTIVE_ENTITLEMENT) {
          toast({
            // @ts-expect-error Type 'Element' is not assignable to type
            title: (
              <div className="flex items-center gap-2">
                <img src={tada} className="w-5 h-5" />
                {t.PAYMENT_CONFIRMATION_DIALOG.LABELS.SUCCESS_MESSAGE}
              </div>
            ),
            variant: "default",
            className: "dark rounded-2xl",
          });
        } else {
          // SYNC_FAILED, or NO_ACTIVE_ENTITLEMENT from RC REST lag or an
          // unmapped product — defer to the webhook.
          showPurchaseProcessingToast();
        }
      },
      onError: () => {
        // Sync request itself failed — same situation as SYNC_FAILED.
        showPurchaseProcessingToast();
      },
    });

    searchParams.delete("iap_purchase");
    setSearchParams(searchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iapPurchase]);

  if (accountSettingsQuery.isLoading || userProfileQuery.isLoading) {
    return <Spinner />;
  }

  const accountSettings = accountSettingsQuery.data;
  const userProfile = userProfileQuery.data;

  if (accountSettingsQuery.error || !accountSettings || !userProfile) {
    return (
      <EmptyState
        icon={CloudOffIcon}
        title={t.ACCOUNT_SETTINGS.ERRORS.LOADING_TITLE}
        message={t.ACCOUNT_SETTINGS.ERRORS.LOADING_DESCRIPTION}
      />
    );
  }

  return (
    <div
      className={`flex flex-col w-full max-w-5xl mx-auto items-center gap-y-4 ${
        isStandalone ? "pb-24" : "pb-16"
      }`}
    >
      <ReferralCard />
      {isMobile && !isStandalone && <AppInstallCard />}
      <PushNotificationSettings />
      <UpdateEmailSection accountSettings={accountSettings} />
      <AccountSettingsForm accountSettings={accountSettings} />
      {isMobile && isStandalone && <AppReloadCard />}
      {userProfile.gender === UserGender.MALE && <MembershipManagement />}
      <ContactSupportCard />
    </div>
  );
}
